/**
 * Versions and the player (docs/SPEC.md §10, §25.4): the tree, one version's
 * `film.json`, a version's manifest (read from `version.manifest_url`, never
 * built here), Go back to this version, and focus.
 *
 * - `GET /v1/ads/{id}/versions` → `[{n, parent_n, kind, say, state,
 *   created_at, is_current}]` in the order of `n`;
 * - `GET …/versions/{n}` → `{n, kind, say, state, is_current, film, brief,
 *   languages, created_at, ready_at}`;
 * - `GET {manifest_url}` → the manifest (`film/manifest.ts`), 409
 *   `version_not_ready` while it bakes;
 * - `POST …/versions/{n}/restore` (202) → the new version as the `version`
 *   event carries it; 409 `change_pending`, `version_not_ready`;
 * - `POST …/focus {scene_id | null}` → `{focus_scene}`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type Manifest, parseManifest } from "../film/manifest";
import { type AdDetail, type AdVersion, adKey } from "./ads";
import { api } from "./client";

export interface VersionLine {
  n: number;
  parent_n: number | null;
  /** `first`, `change`, `author`, `restore`, `brief`. */
  kind: string;
  say: string | null;
  state: string;
  created_at: string | null;
  is_current: boolean;
}

/** The part of `film.json` the page reads (§16): scene names and locks,
 *  the cast's names. */
export interface FilmDoc {
  title?: string;
  /** The film's own language first, then its siblings (§16.10). */
  languages?: string[];
  cast: { id: string; name: string; role?: string }[];
  scenes: { id: string; name: string; seconds: number; lock: boolean }[];
}

export interface VersionDetail {
  n: number;
  kind: string;
  say: string | null;
  state: string;
  is_current: boolean;
  film: FilmDoc;
  brief: unknown;
  /** `{lang: {captions, overlays, …}}`: what each sibling holds (§16.10). */
  languages: unknown;
  created_at: string | null;
  ready_at: string | null;
}

const ad = (id: string) => `/v1/ads/${encodeURIComponent(id)}`;
export const versionsKey = (id: string) => ["ad-versions", id] as const;
export const versionKey = (id: string, n: number) =>
  ["ad-version", id, n] as const;
export const manifestKey = (url: string) => ["manifest", url] as const;

export function useVersions(adId: string, enabled = true) {
  return useQuery({
    queryKey: versionsKey(adId),
    queryFn: () => api<VersionLine[]>(`${ad(adId)}/versions`),
    enabled,
  });
}

export function useVersion(adId: string, n: number | null) {
  return useQuery({
    queryKey: versionKey(adId, n ?? 0),
    queryFn: () => api<VersionDetail>(`${ad(adId)}/versions/${n}`),
    enabled: n !== null,
    staleTime: Infinity,
  });
}

/** A version's manifest by the `manifest_url` its version line carries.
 *  The links in it are short-lived, so a manifest is read again when the
 *  page has held it for ten minutes (before the fifteen-minute links expire);
 *  returning to the tab also refreshes stale links. A version being built is read every
 *  `pollMs` too (its scenes built so far, P28-T06), and answers 409
 *  `version_not_ready` until its first one is. */
export function useManifest(
  url: string | null | undefined,
  pollMs: number | false = false,
) {
  return useQuery({
    queryKey: manifestKey(url ?? ""),
    queryFn: () => api<unknown>(url as string).then(parseManifest),
    enabled: !!url,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: pollMs || 10 * 60_000,
  });
}

/** The manifest of version `n` (compare): from its line's address. */
export function manifestUrlOf(adId: string, n: number): string {
  return `${ad(adId)}/versions/${n}/manifest`;
}

export type { Manifest };

/** Go back to this version (§10): a new version, current at once. */
export function useRestore(adId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (n: number) =>
      api<AdVersion>(`${ad(adId)}/versions/${n}/restore`, { method: "POST" }),
    onSuccess: (version) => {
      queryClient.setQueryData<AdDetail>(adKey(adId), (held) =>
        held ? { ...held, current_version: version } : held,
      );
      void queryClient.invalidateQueries({ queryKey: versionsKey(adId) });
    },
  });
}

/** Focus on a scene, or back to the film (§7.5): a view, remembered for
 *  the page; no version, no charge. */
export function useFocus(adId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sceneId: string | null) =>
      api<{ focus_scene: string | null }>(`${ad(adId)}/focus`, {
        method: "POST",
        body: { scene_id: sceneId },
      }),
    onSuccess: ({ focus_scene }) =>
      queryClient.setQueryData<AdDetail>(adKey(adId), (held) =>
        held ? { ...held, focus_scene } : held,
      ),
  });
}
